import { agreeMark, escapeHtml, memberUrl, voteLabel, votePill } from "../format.js";

export const BANCADA_LABELS = {
  party: "",
  bloc: "bloco",
  governo: "Governo",
  maioria: "Maioria",
  minoria: "Minoria",
  oposicao: "Oposição",
  bancada_feminina: "Banc. feminina",
  other: "",
};

/** Orientation chips (governo, maioria, parties, blocs…) for a roll call. */
export function renderOrientations(orientations, govOfficial) {
  const order = { governo: 0, maioria: 1, minoria: 2, oposicao: 3, bancada_feminina: 4, party: 5, bloc: 6, other: 7 };
  const sorted = [...orientations].sort(
    (a, b) => (order[a.bancada_kind] ?? 9) - (order[b.bancada_kind] ?? 9) || a.bancada_label.localeCompare(b.bancada_label),
  );
  return `<div class="orients">${sorted
    .map((o) => {
      const label = o.party_sigla || o.bancada_label;
      const rawKind = BANCADA_LABELS[o.bancada_kind] ?? "";
      const kind = rawKind.toLowerCase() === String(label).toLowerCase() ? "" : rawKind; // avoid "Governo Governo"
      const proxy = o.source && o.source !== "official" ? `<span class="tag__badge" title="${escapeHtml(o.source)}">proxy</span>` : "";
      return `<span class="orient orient--${escapeHtml(o.bancada_kind)}"><span class="orient__label">${escapeHtml(label)}${kind && o.bancada_kind !== "party" ? ` <span class="meta-muted">${escapeHtml(kind)}</span>` : ""}</span> ${votePill(o.orientation, "pill--sm")}${proxy}</span>`;
    })
    .join("")}</div>${
    !govOfficial && orientations.some((o) => o.bancada_kind === "governo")
      ? `<p class="meta-muted">A posição do governo foi inferida (voto do líder ou maioria da base). Veja a <a class="inline-link" href="/metodologia" data-link>metodologia</a>.</p>`
      : ""
  }`;
}

/** Flatten the per-party groups of a roll-call detail into one row per voter. */
export function flattenGroups(groups) {
  const rows = [];
  for (const g of groups || []) {
    for (const v of g.votes || []) {
      rows.push({
        member: v.member,
        vote: v.vote,
        party_sigla: v.party_sigla || g.party_sigla || "",
        agrees_party_orientation: v.agrees_party_orientation,
        agrees_gov: v.agrees_gov,
      });
    }
  }
  return rows;
}

const collator = new Intl.Collator("pt-BR", { sensitivity: "base" });

function comparator(key) {
  switch (key) {
    case "party":
      return (a, b) => collator.compare(a.party_sigla || "", b.party_sigla || "") || collator.compare(a.member?.name || "", b.member?.name || "");
    case "uf":
      return (a, b) => collator.compare(a.member?.uf || "", b.member?.uf || "") || collator.compare(a.member?.name || "", b.member?.name || "");
    case "vote":
      return (a, b) => collator.compare(voteLabel(a.vote), voteLabel(b.vote)) || collator.compare(a.member?.name || "", b.member?.name || "");
    default:
      return (a, b) => collator.compare(a.member?.name || "", b.member?.name || "");
  }
}

export function sortVotes(rows, sortKey = "name", sortDir = "asc") {
  const sorted = [...rows].sort(comparator(sortKey));
  return sortDir === "desc" ? sorted.reverse() : sorted;
}

const COLUMNS = [
  ["name", "Parlamentar"],
  ["party", "Partido"],
  ["uf", "UF"],
  ["vote", "Voto"],
];

function renderRows(rows) {
  return rows
    .map(
      (r) => `<tr>
      <td><a class="member-link" href="${memberUrl(r.member)}" data-link>${r.member?.photo_url ? `<img class="avatar avatar--sm" src="${escapeHtml(r.member.photo_url)}" alt="" loading="lazy" />` : ""}${escapeHtml(r.member?.name || "—")}</a></td>
      <td>${escapeHtml(r.party_sigla || "—")}</td>
      <td>${escapeHtml(r.member?.uf || "—")}</td>
      <td>${votePill(r.vote, "pill--sm")} <span class="marks" title="partido / governo">${agreeMark(r.agrees_party_orientation)}${agreeMark(r.agrees_gov)}</span></td>
    </tr>`,
    )
    .join("");
}

/**
 * Flat, alphabetical nominal vote table (Parlamentar / Partido / UF / Voto) with click-to-sort headers.
 * Call `bindVoteTable(root, rows)` after inserting the markup.
 */
export function renderVoteTable(rows, { sortKey = "name", sortDir = "asc" } = {}) {
  const sorted = sortVotes(rows, sortKey, sortDir);
  return `
    <div class="table-wrap">
      <table class="table vote-table" data-vote-table>
        <thead><tr>${COLUMNS.map(
          ([key, label]) =>
            `<th data-sort="${key}" aria-sort="${key === sortKey ? (sortDir === "asc" ? "ascending" : "descending") : "none"}">${label}${key === sortKey ? `<span class="vote-table__arrow">${sortDir === "asc" ? "▲" : "▼"}</span>` : ""}</th>`,
        ).join("")}</tr></thead>
        <tbody>${renderRows(sorted)}</tbody>
      </table>
    </div>
    <p class="meta-muted">✓ / ✗ = de acordo / divergente da orientação do partido e do governo.</p>`;
}

export function bindVoteTable(root, rows) {
  const table = root.querySelector("[data-vote-table]");
  if (!table) return;
  let sortKey = "name";
  let sortDir = "asc";
  table.querySelectorAll("th[data-sort]").forEach((th) =>
    th.addEventListener("click", () => {
      const key = th.dataset.sort;
      sortDir = key === sortKey && sortDir === "asc" ? "desc" : "asc";
      sortKey = key;
      table.querySelectorAll("th[data-sort]").forEach((h) => {
        const on = h.dataset.sort === sortKey;
        h.setAttribute("aria-sort", on ? (sortDir === "asc" ? "ascending" : "descending") : "none");
        h.querySelector(".vote-table__arrow")?.remove();
        if (on) h.insertAdjacentHTML("beforeend", `<span class="vote-table__arrow">${sortDir === "asc" ? "▲" : "▼"}</span>`);
      });
      table.querySelector("tbody").innerHTML = renderRows(sortVotes(rows, sortKey, sortDir));
    }),
  );
}
